// 라이브러리 화면 (기획서 4.2, 9.1)
import { getAllBooks, addBook, deleteBook, scanIntegrity } from '../db/books.js';
import { makeInitialProgress } from '../db/progress.js';
import { makeBookId } from '../utils/uuid.js';
import { decodeTextBuffer } from '../core/encoding.js';
import { toast } from '../utils/toast.js';

export class LibraryView {
  constructor(root, { onOpenBook }) {
    this.root = root;
    this.onOpenBook = onOpenBook;
    this.books = [];
    this.damagedIds = new Set();
    this.filter = '';
  }

  async mount() {
    this.root.innerHTML = `
      <div class="library">
        <header class="lib-header">
          <h1>서재</h1>
          <button id="addBtn">＋ 책 추가</button>
        </header>
        <div class="lib-search">
          <input id="libSearch" type="search" placeholder="제목 / 저자 검색" />
        </div>
        <div class="book-grid" id="grid"></div>
        <input id="fileInput" type="file" accept=".epub,.txt" multiple class="hidden" />
      </div>
    `;

    this.gridEl = this.root.querySelector('#grid');
    const fileInput = this.root.querySelector('#fileInput');

    this.root.querySelector('#addBtn').addEventListener('click', () => fileInput.click());
    fileInput.addEventListener('change', (e) => this._handleFiles(e.target.files));

    this.root.querySelector('#libSearch').addEventListener('input', (e) => {
      this.filter = e.target.value.trim().toLowerCase();
      this._renderGrid();
    });

    // 드래그 앤 드롭 (기획서 4.2)
    const lib = this.root.querySelector('.library');
    lib.addEventListener('dragover', (e) => e.preventDefault());
    lib.addEventListener('drop', (e) => {
      e.preventDefault();
      if (e.dataTransfer && e.dataTransfer.files.length) {
        this._handleFiles(e.dataTransfer.files);
      }
    });

    await this._refresh();
  }

  async _refresh() {
    try {
      this.books = await getAllBooks();
      const damaged = await scanIntegrity(); // 기획서 12.3.1 정합성 스캔
      this.damagedIds = new Set(damaged);
      if (damaged.length) {
        toast(`파일이 없는 책 ${damaged.length}권이 손상됨으로 표시됩니다.`);
      }
    } catch (err) {
      console.error(err);
      toast('서재를 불러오지 못했습니다.');
      this.books = [];
    }
    this._renderGrid();
  }

  _renderGrid() {
    const filtered = this.filter
      ? this.books.filter(
          (b) =>
            b.title.toLowerCase().includes(this.filter) ||
            (b.author || '').toLowerCase().includes(this.filter)
        )
      : this.books;

    const cards = filtered.map((b) => this._cardHtml(b)).join('');
    const addCard = `<div class="add-card" id="addCard" title="책 추가">＋</div>`;
    const empty =
      this.books.length === 0
        ? `<div class="empty-hint">아직 추가한 책이 없습니다.<br/>EPUB 또는 TXT 파일을 추가해 보세요.</div>`
        : '';

    this.gridEl.innerHTML = empty + cards + addCard;

    this.gridEl.querySelector('#addCard')?.addEventListener('click', () =>
      this.root.querySelector('#fileInput').click()
    );

    this.gridEl.querySelectorAll('.book-card').forEach((el) => {
      const id = el.dataset.id;
      el.addEventListener('click', () => {
        if (this.damagedIds.has(id)) {
          toast('이 책의 파일을 찾을 수 없습니다. 다시 추가해 주세요.');
          return;
        }
        this.onOpenBook(id);
      });
      // 길게 누르기 / 우클릭 → 컨텍스트 메뉴 (기획서 4.2)
      el.addEventListener('contextmenu', (e) => {
        e.preventDefault();
        this._showContextMenu(e.clientX, e.clientY, id);
      });
      let lpTimer = null;
      el.addEventListener('touchstart', (e) => {
        const t = e.touches[0];
        lpTimer = setTimeout(() => this._showContextMenu(t.clientX, t.clientY, id), 550);
      });
      el.addEventListener('touchend', () => lpTimer && clearTimeout(lpTimer));
      el.addEventListener('touchmove', () => lpTimer && clearTimeout(lpTimer));
    });
  }

  _cardHtml(b) {
    const pct = Math.round((b.progress || 0) * 100);
    const statusText =
      b.status === 'finished' ? '완독' : pct > 0 ? `${pct}%` : '시작 전';
    const damaged = this.damagedIds.has(b.id);
    const cover = b.coverDataURL
      ? `<img src="${b.coverDataURL}" alt="" />`
      : `<div class="placeholder-title">${escapeHtml(b.title)}</div>`;
    return `
      <div class="book-card ${damaged ? 'damaged' : ''}" data-id="${b.id}">
        <div class="book-cover">
          ${cover}
          <span class="fmt-badge">${b.format.toUpperCase()}</span>
          ${damaged ? '<span class="damaged-badge">손상됨</span>' : ''}
          <div class="cover-progress" style="width:${pct}%"></div>
        </div>
        <div class="book-meta">
          <div class="book-title">${escapeHtml(b.title)}</div>
          <div class="book-progress-text">${statusText}</div>
        </div>
      </div>`;
  }

  _showContextMenu(x, y, bookId) {
    document.querySelector('.ctx-menu')?.remove();
    const menu = document.createElement('div');
    menu.className = 'ctx-menu';
    menu.style.left = Math.min(x, window.innerWidth - 160) + 'px';
    menu.style.top = Math.min(y, window.innerHeight - 100) + 'px';
    menu.innerHTML = `<button class="danger" data-act="delete">삭제</button>`;
    document.body.appendChild(menu);

    const close = () => {
      menu.remove();
      document.removeEventListener('click', close);
    };
    setTimeout(() => document.addEventListener('click', close), 0);

    menu.querySelector('[data-act="delete"]').addEventListener('click', async () => {
      close();
      if (confirm('이 책을 삭제할까요? 읽던 위치도 함께 삭제됩니다.')) {
        await deleteBook(bookId);
        await this._refresh();
        toast('삭제했습니다.');
      }
    });
  }

  async _handleFiles(fileList) {
    const files = Array.from(fileList);
    for (const file of files) {
      const lower = file.name.toLowerCase();
      const isEpub = lower.endsWith('.epub');
      const isTxt = lower.endsWith('.txt');
      if (!isEpub && !isTxt) {
        toast(`${file.name}: 지원하지 않는 형식입니다.`);
        continue;
      }
      try {
        if (isTxt) {
          await this._addTxt(file);
        } else {
          await this._addEpub(file);
        }
      } catch (err) {
        console.error(err);
        toast(`${file.name} 추가 실패: 저장 공간을 확인하거나 다시 시도하세요.`);
      }
    }
    await this._refresh();
  }

  async _addTxt(file) {
    const buf = await file.arrayBuffer();
    // 인코딩 감지 + NFC 정규화 (기획서 8.4.3). 디코드 검증만, Block 은 열 때 생성.
    const { text } = decodeTextBuffer(buf);
    if (!text || text.trim().length === 0) {
      toast(`${file.name}: 내용을 읽을 수 없습니다.`);
      return;
    }
    const id = makeBookId();
    const title = file.name.replace(/\.txt$/i, '');
    const meta = {
      id,
      title,
      author: null,
      format: 'txt',
      coverDataURL: null,
      addedAt: Date.now(),
      lastReadAt: null,
      fileSize: file.size,
      status: 'unread',
    };
    // 원본 Blob 그대로 저장 (인코딩 재감지는 열 때 다시 — 일관성)
    await addBook(meta, file, makeInitialProgress(id, 'txt'));
    toast(`'${title}' 추가됨`);
  }

  async _addEpub(file) {
    const ePub = (await import('epubjs')).default;
    const buf = await file.arrayBuffer();
    let title = file.name.replace(/\.epub$/i, '');
    let author = null;
    let coverDataURL = null;
    try {
      const book = ePub(buf);
      await book.ready;
      const md = book.packaging?.metadata || {};
      title = md.title || title;
      author = md.creator || null;
      try {
        const coverUrl = await book.coverUrl();
        if (coverUrl) {
          const blob = await (await fetch(coverUrl)).blob();
          coverDataURL = await blobToDataURL(blob);
        }
      } catch {
        /* 표지 없음 — placeholder 사용 */
      }
      book.destroy();
    } catch {
      toast(`${file.name}: EPUB 메타 파싱 실패, 파일명으로 추가합니다.`);
    }
    const id = makeBookId();
    const meta = {
      id,
      title,
      author,
      format: 'epub',
      coverDataURL,
      addedAt: Date.now(),
      lastReadAt: null,
      fileSize: file.size,
      status: 'unread',
    };
    await addBook(meta, file, makeInitialProgress(id, 'epub'));
    toast(`'${title}' 추가됨`);
  }

  unmount() {
    document.querySelector('.ctx-menu')?.remove();
  }
}

function escapeHtml(s) {
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function blobToDataURL(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(r.error);
    r.readAsDataURL(blob);
  });
}
